import {createSlice, PayloadAction} from "@reduxjs/toolkit";
import {ITrackPoint} from "../../../models/ITrackPoint";
import dayjs from "dayjs";

/**
 * Обогащение маршрута дорожным графом (Valhalla), приходит чанками с
 * map_history:chunk.
 * snapped — массив той же длины, что list: координата точки, привязанная
 *   к дороге, либо null (точка далеко от дороги — рисуем сырую).
 * reconstructed — восстановленные участки вырезанных спуф-разрывов.
 */
export interface IReconstructedSegment {
    after_point_id: string;
    before_point_id: string;
    /** Геометрия по дорожной сети: [[lat, lng], ...] */
    geometry: [number, number][];
    /** 0.9 — подтверждено одометром, 0.6 — только скоростной валидацией */
    confidence: number;
}

export interface IRouteEnrichment {
    snapped: ([number, number] | null)[];
    reconstructed: IReconstructedSegment[];
}

export const EMPTY_ROUTE_ENRICHMENT: IRouteEnrichment = {
    snapped: [],
    reconstructed: [],
};

/** bbox всего дня — приходит в map_history:meta, для фокуса карты по
 *  полному треку (а не по первому чанку). */
export interface IRouteBounds {
    minLat: number;
    minLon: number;
    maxLat: number;
    maxLon: number;
}

/** Конкатенация обогащения двух чанков (snapped и reconstructed подряд). */
function concatEnrichment(
    a: IRouteEnrichment,
    b: IRouteEnrichment,
): IRouteEnrichment {
    return {
        snapped: [...a.snapped, ...b.snapped],
        reconstructed: [...a.reconstructed, ...b.reconstructed],
    };
}

interface IMapHistoryState {
    list: ITrackPoint[];
    isLoading: boolean;
    /** true только после map_history:done — статистику считаем по нему. */
    isComplete: boolean;
    currentMachineryId: string | null;
    activePointId: string | null;
    isConnect: boolean;
    selectedDateStr: string;
    routeVersion: number;
    filterOutliers: boolean,
    enrichment: IRouteEnrichment;
    /** bbox всего дня для фокуса карты (приходит в meta до чанков). */
    bounds: IRouteBounds | null;
    /**
     * Буфер тихого (live) перезапроса при включённом фильтре. Чанки копятся
     * здесь, не трогая list, и атомарно подменяют list по commit — без
     * мигания маршрута и без fitBounds.
     */
    staging: ITrackPoint[];
    stagingEnrichment: IRouteEnrichment;
}

const initialState: IMapHistoryState = {
    list: [],
    isLoading: false,
    isComplete: false,
    currentMachineryId: null,
    activePointId: null,
    isConnect: false,
    selectedDateStr: dayjs().format("YYYY-MM-DD"),
    routeVersion: 0,
    filterOutliers: true,
    enrichment: EMPTY_ROUTE_ENRICHMENT,
    bounds: null,
    staging: [],
    stagingEnrichment: EMPTY_ROUTE_ENRICHMENT,
};

export const MapHistorySlice = createSlice({
    name: "map_history_slice",
    initialState,
    reducers: {
        // ── Прогрессивная загрузка (обычный запрос) ──────────────────────
        /**
         * Дописывание чанка истории (обычный запрос). Линия дорисовывается
         * по мере прихода чанков. isLoading держим true до finishHistoryLoad.
         * Порядок и отсутствие дублей гарантирует хук (проверка seq+index),
         * поэтому здесь просто append без передедупликации — иначе поедет
         * выравнивание enrichment.snapped по индексам.
         */
        appendHistoryChunk: (
            state,
            action: PayloadAction<{ points: ITrackPoint[]; enrichment: IRouteEnrichment }>,
        ) => {
            state.list.push(...action.payload.points);
            state.enrichment = concatEnrichment(
                state.enrichment,
                action.payload.enrichment ?? EMPTY_ROUTE_ENRICHMENT,
            );
        },
        /** Завершение загрузки (map_history:done) — гасим спиннер, поднимаем флаг. */
        finishHistoryLoad: (state) => {
            state.isLoading = false;
            state.isComplete = true;
        },

        // ── Тихий live-refresh (фильтр включён) через staging ────────────
        startSilentRefresh: (state) => {
            state.staging = [];
            state.stagingEnrichment = EMPTY_ROUTE_ENRICHMENT;
        },
        appendSilentChunk: (
            state,
            action: PayloadAction<{ points: ITrackPoint[]; enrichment: IRouteEnrichment }>,
        ) => {
            state.staging.push(...action.payload.points);
            state.stagingEnrichment = concatEnrichment(
                state.stagingEnrichment,
                action.payload.enrichment ?? EMPTY_ROUTE_ENRICHMENT,
            );
        },
        /**
         * Атомарная подмена list на собранный staging. НЕ инкрементит
         * routeVersion → routeKey не меняется → карта не делает fitBounds
         * и не уносит пользователя от места, на которое он смотрит.
         */
        commitSilentRefresh: (state) => {
            state.list = state.staging;
            state.enrichment = state.stagingEnrichment;
            state.staging = [];
            state.stagingEnrichment = EMPTY_ROUTE_ENRICHMENT;
            state.isComplete = true;
            state.isLoading = false;
        },

        /**
         * Дописывание одной live-точки (фильтр выключен — сырой режим).
         * Дубль по id игнорируется (broadcast может догнать перезапрос).
         */
        appendHistoryTrackPoint: (state, action: PayloadAction<ITrackPoint>) => {
            const p = action.payload;
            if (state.list.some((x) => x.id === p.id)) return;
            state.list.push(p);
        },
        setCurrentMachineryIdFromMapHistory: (state, action: PayloadAction<string>) => {
            state.currentMachineryId = action.payload;
        },
        setActivePointId: (state, action: PayloadAction<string | null>) => {
            state.activePointId = action.payload;
        },
        setMapHistoryConnect: (state, action: PayloadAction<boolean>) => {
            state.isConnect = action.payload;
        },
        /** bbox всего дня из meta — для разового фокуса карты. */
        setHistoryBounds: (state, action: PayloadAction<IRouteBounds | null>) => {
            state.bounds = action.payload;
        },
        clearHistory: (state) => {
            state.list = [];
            state.enrichment = EMPTY_ROUTE_ENRICHMENT;
            state.staging = [];
            state.stagingEnrichment = EMPTY_ROUTE_ENRICHMENT;
            state.activePointId = null;
            state.bounds = null;
            state.routeVersion += 1;
            state.isLoading = true;     // показываем, что грузим новый маршрут
            state.isComplete = false;
        },
        setHistoryLoading: (state, action: PayloadAction<boolean>) => {
            state.isLoading = action.payload;
        },
        setHistorySelectedDateStr: (state, action: PayloadAction<string>) => {
            state.selectedDateStr = action.payload;
        },
        setFilterOutliers: (state, action: PayloadAction<boolean>) => {
            state.filterOutliers = action.payload;
        },
    },
});

export const {
    appendHistoryChunk,
    finishHistoryLoad,
    startSilentRefresh,
    appendSilentChunk,
    commitSilentRefresh,
    appendHistoryTrackPoint,
    setCurrentMachineryIdFromMapHistory,
    setActivePointId,
    setMapHistoryConnect,
    setHistoryBounds,
    clearHistory,
    setHistoryLoading,
    setHistorySelectedDateStr,
    setFilterOutliers,
} = MapHistorySlice.actions;
export default MapHistorySlice.reducer;